import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Alert,
    Button,
    Grid,
} from '@mui/material';
import { Add, TrendingUp, TrendingDown } from '@mui/icons-material';
import { MainLayout } from '../components/MainLayout';
import { investmentsService, ConsolidatedHolding } from '../services/investments-service';
import { useNavigate } from '@tanstack/react-router';
import { formatCurrency as fmtCurrency } from '../utils/currency';
import { useBaseCurrency } from '../stores/currency-store';

export const PortfolioPage: React.FC = () => {
    const baseCurrency = useBaseCurrency();
    const navigate = useNavigate();
    const [holdings, setHoldings] = useState<ConsolidatedHolding[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadPortfolio();
    }, []);

    const loadPortfolio = async () => {
        try {
            setLoading(true);
            const response = await investmentsService.getConsolidatedHoldings();
            setHoldings(response.data?.holdings || []);
        } catch (err) {
            console.error('Failed to load portfolio:', err);
            setError('Failed to load portfolio data. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    // A consolidated view spans accounts in different currencies, so it reports
    // in the base currency. It was hardcoded to USD, which mislabelled the
    // entire portfolio for a CAD household.
    const formatCurrency = (amount: string | number) =>
        fmtCurrency(amount, baseCurrency, { base: baseCurrency });

    const calculateUnrealizedGL = (holding: ConsolidatedHolding) => {
        // Mocking market price for now as we don't have real-time price yet
        // In a real app, we would fetch this from an API
        const mockMarketPrice = parseFloat(holding.acb_per_share) * (1 + (Math.random() * 0.2 - 0.1)); // +/- 10%
        const marketValue = mockMarketPrice * parseFloat(holding.total_shares);
        const costBasis = parseFloat(holding.total_cost_basis);
        const gainLoss = marketValue - costBasis;
        const gainLossPercent = (gainLoss / costBasis) * 100;

        return {
            marketPrice: mockMarketPrice,
            marketValue,
            gainLoss,
            gainLossPercent,
        };
    };

    return (
        <MainLayout>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                        Portfolio Tracking
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Track your investments and Adjusted Cost Basis (ACB) across all accounts.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => navigate({ to: '/accounts' })} // Navigate to accounts to add transaction for now
                >
                    Add Transaction
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : holdings.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 4 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No investments found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Add investment accounts and transactions to start tracking your portfolio.
                    </Typography>
                    <Button variant="outlined" onClick={() => navigate({ to: '/accounts' })}>
                        Go to Accounts
                    </Button>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TableContainer component={Paper} sx={{ borderRadius: 4, overflow: 'hidden' }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Symbol</TableCell>
                                        <TableCell align="right">Shares</TableCell>
                                        <TableCell align="right">Avg. Cost (ACB)</TableCell>
                                        <TableCell align="right">Total Cost</TableCell>
                                        <TableCell align="right">Market Price (Est)</TableCell>
                                        <TableCell align="right">Market Value (Est)</TableCell>
                                        <TableCell align="right">Unrealized G/L</TableCell>
                                        <TableCell>Accounts</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {holdings.map((holding) => {
                                        const { marketPrice, marketValue, gainLoss, gainLossPercent } = calculateUnrealizedGL(holding);
                                        const isPositive = gainLoss >= 0;

                                        return (
                                            <TableRow key={holding.symbol} hover>
                                                <TableCell>
                                                    <Typography fontWeight="bold">{holding.symbol}</Typography>
                                                </TableCell>
                                                <TableCell align="right">{parseFloat(holding.total_shares).toLocaleString()}</TableCell>
                                                <TableCell align="right">{formatCurrency(holding.acb_per_share)}</TableCell>
                                                <TableCell align="right">{formatCurrency(holding.total_cost_basis)}</TableCell>
                                                <TableCell align="right">{formatCurrency(marketPrice)}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                    {formatCurrency(marketValue)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, color: isPositive ? 'success.main' : 'error.main' }}>
                                                        {isPositive ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />}
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {formatCurrency(gainLoss)}
                                                            </Typography>
                                                            <Typography variant="caption">
                                                                {isPositive ? '+' : ''}{gainLossPercent.toFixed(2)}%
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                        {holding.account_names.map((name, idx) => (
                                                            <Chip key={idx} label={name} size="small" variant="outlined" />
                                                        ))}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            )}
        </MainLayout>
    );
};
